import { useEffect, useRef, useState } from 'react'
import { api } from '../lib/api'
import { markUnlocked } from '../lib/security'
import { isNative, biometricUnlock } from '../lib/native'

/** Sanctum Lock overlay — passcode verified server-side; content stays hidden beneath. */
export default function LockScreen({ onUnlock }: { onUnlock: () => void }) {
  const [code, setCode] = useState('')
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => { inputRef.current?.focus() }, [])

  // Native shells: offer Face ID / fingerprint as the unlock (screen-privacy
  // overlay — data access stays JWT-gated regardless, so device biometry is
  // an equivalent-or-stronger assurance than the 4-8 digit passcode).
  const [bioAvailable] = useState(isNative)
  const tryBiometric = async () => {
    if (await biometricUnlock('Unlock your family constellation')) { markUnlocked(); onUnlock() }
  }

  const tryUnlock = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!code || busy) return
    setBusy(true); setErr('')
    try {
      await api.verifyLock(code)
      markUnlocked()
      onUnlock()
    } catch (ex) {
      const m = ex instanceof Error && ex.message.includes('429')
        ? 'Too many attempts — wait a few minutes.'
        : 'Wrong passcode.'
      setErr(m); setCode('')
    } finally { setBusy(false) }
  }

  return (
    <div className="fixed inset-0 z-[100] bg-void-950 flex items-center justify-center px-4">
      <div className="aurora aurora-gold aurora-drift w-[300px] h-[300px] -top-16 -right-10 opacity-15" />
      <div className="text-center max-w-xs w-full">
        <div className="text-5xl mb-4">🕯️</div>
        <h1 className="font-display text-xl font-bold text-slate-100 mb-1">The sanctum is sealed</h1>
        <p className="text-xs text-slate-500 mb-6">Enter your passcode to return to the constellation.</p>
        <form onSubmit={tryUnlock}>
          <input ref={inputRef} type="password" inputMode="numeric" autoComplete="off" value={code}
            onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
            className="input-dark text-center text-2xl tracking-[0.5em] mb-3" placeholder="••••" />
          {err && <p className="text-xs text-red-400 mb-3">{err}</p>}
          <button type="submit" disabled={busy || code.length < 4} className="btn-gold w-full !py-2.5">
            {busy ? 'Unsealing…' : 'Unlock'}
          </button>
        </form>
        {bioAvailable && (
          <button onClick={tryBiometric} className="btn-ghost w-full !py-2.5 mt-2 text-sm">🪪 Unlock with Face ID / fingerprint</button>
        )}
        <button onClick={async () => { const { clearAuth } = await import('../lib/auth'); clearAuth(); window.location.href = '/login' }}
          className="text-[11px] text-slate-600 hover:text-slate-400 mt-4">Sign out instead</button>
      </div>
    </div>
  )
}
